package br.com.vvrefeicoes.portal.feature.users

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppModalCard
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.components.SearchableSelect
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.spreadsheet.isSpreadsheetImportSupported
import br.com.vvrefeicoes.portal.core.util.spreadsheet.pickSpreadsheetGrid
import br.com.vvrefeicoes.portal.domain.model.UserImportPreview
import br.com.vvrefeicoes.portal.domain.model.UserImportResult
import br.com.vvrefeicoes.portal.domain.model.UserImportRow
import br.com.vvrefeicoes.portal.domain.model.UserProfile
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.ShieldCheck
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.users_cancel
import portalvv.shared.generated.resources.users_import
import portalvv.shared.generated.resources.users_import_created
import portalvv.shared.generated.resources.users_import_duplicates
import portalvv.shared.generated.resources.users_import_error
import portalvv.shared.generated.resources.users_import_loaded
import portalvv.shared.generated.resources.users_import_pick
import portalvv.shared.generated.resources.users_import_previewing
import portalvv.shared.generated.resources.users_import_profile
import portalvv.shared.generated.resources.users_import_profile_placeholder
import portalvv.shared.generated.resources.users_import_result_title
import portalvv.shared.generated.resources.users_import_skipped
import portalvv.shared.generated.resources.users_import_title

@Composable
fun UserImportDialog(
    profiles: List<UserProfile>,
    importing: Boolean,
    error: String?,
    onImport: (String, List<UserImportRow>) -> Unit,
    onPreview: suspend (List<UserImportRow>) -> UserImportPreview,
    onDismiss: () -> Unit,
    result: UserImportResult?,
) {
    val scope = rememberCoroutineScope()
    val profileOptions = remember(profiles) { profiles.map { it.name } }
    val profileByName = remember(profiles) { profiles.associateBy { it.name } }
    var selectedProfile by remember { mutableStateOf("") }
    var preview by remember { mutableStateOf<UserImportPreviewData?>(null) }
    var remotePreview by remember { mutableStateOf<UserImportPreview?>(null) }
    var picking by remember { mutableStateOf(false) }
    var previewing by remember { mutableStateOf(false) }
    var localError by remember { mutableStateOf<String?>(null) }
    val supported = isSpreadsheetImportSupported()
    val profileId = profileByName[selectedProfile]?.id
    val canImport = profileId != null && preview != null && remotePreview != null && !importing && !previewing

    AppModalCard(
        title = stringResource(Res.string.users_import_title),
        onDismiss = onDismiss,
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().widthIn(max = 560.dp),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space16),
        ) {
            SearchableSelect(
                leadingIcon = Lucide.ShieldCheck,
                options = profileOptions,
                selected = selectedProfile,
                onSelect = { selectedProfile = it },
                modifier = Modifier.fillMaxWidth(),
                placeholder = stringResource(Res.string.users_import_profile_placeholder),
                searchPlaceholder = stringResource(Res.string.users_import_profile),
                searchable = true,
                underlined = true,
            )

            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8, Alignment.End),
            ) {
                AppButton(
                    text = stringResource(Res.string.users_import_pick),
                    onClick = {
                        scope.launch {
                            picking = true
                            previewing = false
                            localError = null
                            preview = null
                            remotePreview = null
                            try {
                                val grid = pickSpreadsheetGrid()
                                if (grid != null) {
                                    val localPreview = parseUserImportRows(grid)
                                    preview = localPreview
                                    previewing = true
                                    remotePreview = onPreview(localPreview.rows)
                                }
                            } catch (throwable: Throwable) {
                                preview = null
                                remotePreview = null
                                localError = throwable.message ?: "Não foi possível ler a planilha."
                            } finally {
                                picking = false
                                previewing = false
                            }
                        }
                    },
                    enabled = supported && !picking && !importing && !previewing,
                    variant = PortalButtonVariant.Outlined,
                )
                if (!supported) {
                    Text(
                        text = stringResource(Res.string.users_import_error),
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.error,
                    )
                }
            }

            if (preview != null) {
                if (previewing) {
                    Text(
                        text = stringResource(Res.string.users_import_previewing),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                } else {
                    remotePreview?.let { counted ->
                        Text(
                            text = stringResource(Res.string.users_import_loaded, counted.ready),
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurface,
                        )
                        Text(
                            text = stringResource(Res.string.users_import_duplicates, counted.skippedDuplicate),
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                    }
                }
            }

            (localError ?: error)?.let { message ->
                Text(
                    text = message,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.error,
                )
            }

            result?.let { data ->
                Text(
                    text = stringResource(Res.string.users_import_result_title),
                    style = MaterialTheme.typography.titleSmall,
                )
                Text(
                    text = stringResource(Res.string.users_import_created, data.created),
                    style = MaterialTheme.typography.bodyMedium,
                )
                Text(
                    text =
                        stringResource(
                            Res.string.users_import_skipped,
                            data.skippedMissingEmail,
                            data.skippedDuplicate,
                            data.skippedExisting,
                            data.invalid,
                        ),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                data.errors.take(3).forEach { message ->
                    Text(
                        text = message,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8, Alignment.End),
            ) {
                AppButton(
                    text = stringResource(Res.string.users_cancel),
                    onClick = onDismiss,
                    variant = PortalButtonVariant.Text,
                    enabled = !importing,
                )
                AppButton(
                    text = stringResource(Res.string.users_import),
                    onClick = {
                        profileId?.let { id -> preview?.let { onImport(id, it.rows) } }
                    },
                    enabled = canImport,
                    variant = PortalButtonVariant.Filled,
                )
            }
        }
    }
}
